// src/pages/PlotSite.jsx
import React, { useEffect, useRef, useState } from 'react';
import { SelectedChannelsProvider, useSelectedChannels } from '../contexts/SelectedChannelsContext';
import { SelectedSiteProvider, useSelectedSite } from '../contexts/SelectedSiteContext';
import SiteSelector from '../components/SiteSelector/SiteSelector';
import DateIntervalSelector from '../components/DateIntervalSelector/DateIntervalSelector';
import ChannelPickerPanel, { defaultLoadChannels } from '../components/ChannelPickerPanel/ChannelPickerPanel';
import PlotComponent from '../components/PlotComponent/PlotComponent';
import PlotViewComponent from '../components/PlotViewComponent/PlotViewComponent';

export default function PlotSite(props) {
  // Discard user-specific selections, in-flight requests and views on sign-in changes.
  return (
    <SelectedSiteProvider key={`${props.backendUrl}:${props.user.token}`} initialSiteId="">
      <SelectedChannelsProvider>
        <PlotPage {...props} />
      </SelectedChannelsProvider>
    </SelectedSiteProvider>
  );
}

function PlotPage({
  backendUrl,
  user,
  loadSites,
  loadChannels = defaultLoadChannels,
  loadData,
}) {
  const [interval, setInterval] = useState({
    start: new Date(Date.now() - 24 * 3600 * 1000),
    end: new Date(),
  });

  const [collapsed, setCollapsed] = useState(false);
  const [showPsd, setShowPsd] = useState(false);
  const [showLegends, setShowLegends] = useState(true);
  const [channelSnapshot, setChannelSnapshot] = useState(null);
  const [channelLoading, setChannelLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [viewRevision, setViewRevision] = useState(0);
  const { selectedSite, setSelectedSite } = useSelectedSite();
  const { selectedIds, setAvailableChannels } = useSelectedChannels();
  const mounted = useRef(true);
  const restoreRequest = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      restoreRequest.current += 1;
    };
  }, []);

  const changeSite = (siteId) => {
    restoreRequest.current += 1;
    setChannelSnapshot(null);
    setAvailableChannels([]);
    setSelectedSite(siteId);
  };

  const applyView = async (view) => {
    const request = ++restoreRequest.current;
    // The view endpoint rechecks membership; the channel endpoint does too.
    // Prepare everything first, so a failed restore leaves current settings intact.
    const channels = await loadChannels({ backendUrl, user, siteId: view.siteId });
    if (!mounted.current || request !== restoreRequest.current) return;
    const availableIds = new Set(channels.map(channel => channel.id));
    if (view.channelIds.some(id => !availableIds.has(id))) {
      throw new Error('Some saved channels are no longer available. The view was not loaded.');
    }
    setAvailableChannels(channels, view.channelIds);
    setChannelSnapshot({ siteId: view.siteId, channels });
    setSelectedSite(view.siteId);
    setInterval({ start: new Date(view.interval.start), end: new Date(view.interval.end) });
    setShowPsd(view.showPsd);
    setShowLegends(view.showLegends);
    setViewRevision(revision => revision + 1);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 16,
        minHeight: '100vh',
        boxSizing: 'border-box',
      }}
    >
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <SiteSelector backendUrl={backendUrl} user={user} loadSites={loadSites} disabled={restoring} onChange={changeSite} />
          <DateIntervalSelector interval={interval} onChange={setInterval} disabled={restoring} />

            <PlotViewComponent
              backendUrl={backendUrl}
              user={user}
              currentSettings={{
                version: 1,
                siteId: selectedSite,
                channelIds: Array.from(selectedIds),
                interval,
                showPsd,
                showLegends,
              }}
              onApply={applyView}
              onApplyingChange={setRestoring}
              disabled={restoring || channelLoading}
            />
        </div>
          {/* MAIN SPLIT: left auto / right fills. When collapsed, left = 0 */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: collapsed ? '0 1fr' : 'minmax(240px, 420px) 1fr',
              gap: 12,
              flex: 1,
              minHeight: 0, // allow children to shrink
            }}
          >
            {/* LEFT: channel picker wrapper we can collapse */}
            <aside
              style={{
                overflow: 'auto',
                borderRight: collapsed ? 'none' : '1px solid #ddd',
                paddingRight: collapsed ? 0 : 12,
                opacity: collapsed ? 0 : 1,
                transition: 'opacity 120ms ease, width 120ms ease, padding 120ms ease, border 120ms ease',
                pointerEvents: collapsed ? 'none' : 'auto',
              }}
              aria-hidden={collapsed}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <h3 style={{ margin: 0 }}>Channels</h3>
                <button type="button" onClick={() => setCollapsed(true)} aria-label="Collapse channel panel">
                  Hide
                </button>
              </div>

              <ChannelPickerPanel
                backendUrl={backendUrl}
                user={user}
                loadChannels={loadChannels}
                channelSnapshot={channelSnapshot}
                onLoadingChange={setChannelLoading}
                disabled={restoring}
              />
            </aside>

            {/* RIGHT: plot fills the rest */}
            <section
              style={{
                minWidth: 0, // IMPORTANT: let chart grow when sidebar collapses
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              {collapsed && (
                <div style={{ marginBottom: 8 }}>
                  <button type="button" onClick={() => setCollapsed(false)} aria-label="Expand channel panel">
                    Show channels
                  </button>
                </div>
              )}

              <PlotComponent
                key={`${selectedSite}:${viewRevision}`}
                backendUrl={backendUrl}
                user={user}
                interval={interval}
                onIntervalChange={(s, e) => setInterval({ start: s, end: e })}
                loadData={loadData}
                showPsd={showPsd}
                onShowPsdChange={setShowPsd}
                showLegends={showLegends}
                onShowLegendsChange={setShowLegends}
                disabled={restoring}
              />
            </section>
          </div>
    </div>
  );
}
